/**
 * Keeps focusable content out of the tab order while its slide is aria-hidden.
 *
 * Horizon's slideshow marks off-screen slides `aria-hidden="true"` (see the five
 * call sites in slideshow.js) but leaves their links focusable, so keyboard users
 * tab into product cards that screen readers announce as nothing. Lighthouse flags
 * this as "ARIA hidden element must not be focusable or contain focusable
 * elements".
 *
 * Written as an observer rather than a patch to slideshow.js so a Horizon update
 * doesn't silently drop it, and so all five aria-hidden paths are covered at once.
 *
 * Deliberately NOT the `inert` attribute, which would be the tidier fix: inert also
 * blocks pointer events, and a slide can be aria-hidden while still partly on
 * screen and clickable. Mirroring tabindex fixes the tab order without changing
 * what a mouse can reach.
 */
const FOCUSABLE = 'a[href], area[href], button, input, select, textarea, iframe, [tabindex]';

/** Original tabindex per element, so restoring never invents a value. */
const previous = new WeakMap();

function sync(slide) {
  const hidden = slide.getAttribute('aria-hidden') === 'true';

  for (const el of slide.querySelectorAll(FOCUSABLE)) {
    if (hidden) {
      if (!previous.has(el)) previous.set(el, el.getAttribute('tabindex'));
      el.setAttribute('tabindex', '-1');
      continue;
    }

    if (!previous.has(el)) continue;
    const original = previous.get(el);
    if (original === null) el.removeAttribute('tabindex');
    else el.setAttribute('tabindex', original);
    previous.delete(el);
  }
}

const attributes = new MutationObserver((records) => {
  for (const record of records) sync(record.target);
});

function watch(slide) {
  attributes.observe(slide, { attributes: true, attributeFilter: ['aria-hidden'] });
  sync(slide);
}

function scan(root) {
  if (root.matches?.('slideshow-slide')) watch(root);
  root.querySelectorAll?.('slideshow-slide').forEach(watch);
}

scan(document);

/* Carousels also arrive after load — Section Rendering API swaps, quick-add
   dialogs, cart drawer recommendations. */
new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (node.nodeType === Node.ELEMENT_NODE) scan(node);
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });
